import { z } from 'zod'

export const CRM_PERSON_READ_CONTRACT_VERSION = '1.0.0' as const

const rowSchema = z.record(z.unknown())
const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

export const crmPersonReadQuerySchema = z.object({
  orgId: optionalQueryString,
  org_id: optionalQueryString,
  q: optionalQueryString,
  membership_status: optionalQueryString,
  tier: optionalQueryString,
  engagement_band: optionalQueryString,
  page: optionalQueryString,
  page_size: optionalQueryString,
  pageSize: optionalQueryString,
  sort: optionalQueryString,
  limit: optionalQueryString,
  cursor: optionalQueryString,
  type: optionalQueryString,
}).passthrough()

export const crmPersonReadSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  db1Written: z.literal(false),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const crmPersonListResponseSchema = z.object({
  contract_version: z.literal(CRM_PERSON_READ_CONTRACT_VERSION),
  people: z.object({
    items: z.array(rowSchema),
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
  }).passthrough(),
  sideEffects: crmPersonReadSideEffectsSchema,
}).passthrough()

export const crmPersonHeaderResponseSchema = z.object({
  contract_version: z.literal(CRM_PERSON_READ_CONTRACT_VERSION),
  person: rowSchema.nullable(),
  sideEffects: crmPersonReadSideEffectsSchema,
}).passthrough()

export const crmPersonTimelineResponseSchema = z.object({
  contract_version: z.literal(CRM_PERSON_READ_CONTRACT_VERSION),
  timeline: z.object({
    events: z.array(rowSchema),
    nextCursor: z.string().nullable(),
  }).passthrough(),
  sideEffects: crmPersonReadSideEffectsSchema,
}).passthrough()

export const crmPersonParticipationResponseSchema = z.object({
  contract_version: z.literal(CRM_PERSON_READ_CONTRACT_VERSION),
  participation: z.array(rowSchema),
  sideEffects: crmPersonReadSideEffectsSchema,
}).passthrough()

export const crmPersonFeedbackResponseSchema = z.object({
  contract_version: z.literal(CRM_PERSON_READ_CONTRACT_VERSION),
  feedback: z.array(rowSchema),
  sideEffects: crmPersonReadSideEffectsSchema,
}).passthrough()

export const crmPersonMembershipsResponseSchema = z.object({
  contract_version: z.literal(CRM_PERSON_READ_CONTRACT_VERSION),
  memberships: z.array(rowSchema),
  history: z.array(rowSchema),
  sideEffects: crmPersonReadSideEffectsSchema,
}).passthrough()

export const crmPersonCommerceResponseSchema = z.object({
  contract_version: z.literal(CRM_PERSON_READ_CONTRACT_VERSION),
  commerce: z.array(rowSchema),
  sideEffects: crmPersonReadSideEffectsSchema,
}).passthrough()

export const crmPersonCreditsResponseSchema = z.object({
  contract_version: z.literal(CRM_PERSON_READ_CONTRACT_VERSION),
  credits: z.object({
    balances: z.array(rowSchema),
    ledger: z.array(rowSchema),
  }).passthrough(),
  sideEffects: crmPersonReadSideEffectsSchema,
}).passthrough()

export const crmPersonEnrichmentResponseSchema = z.object({
  contract_version: z.literal(CRM_PERSON_READ_CONTRACT_VERSION),
  enrichment: z.array(rowSchema),
  sideEffects: crmPersonReadSideEffectsSchema,
}).passthrough()

export const crmPersonArtifactsResponseSchema = z.object({
  contract_version: z.literal(CRM_PERSON_READ_CONTRACT_VERSION),
  artifacts: z.array(rowSchema),
  sideEffects: crmPersonReadSideEffectsSchema,
}).passthrough()

export const crmPersonIdentityResponseSchema = z.object({
  contract_version: z.literal(CRM_PERSON_READ_CONTRACT_VERSION),
  aliases: z.array(rowSchema),
  alternateAttributes: z.array(rowSchema),
  matchCandidates: z.array(rowSchema),
  sideEffects: crmPersonReadSideEffectsSchema,
}).passthrough()

export const crmPersonRecommendationsResponseSchema = z.object({
  contract_version: z.literal(CRM_PERSON_READ_CONTRACT_VERSION),
  recommendations: z.array(rowSchema),
  sideEffects: crmPersonReadSideEffectsSchema,
}).passthrough()

export const crmPersonReadErrorEnvelopeSchema = z.object({
  contract_version: z.string().min(1),
  error: z.object({
    code: z.string().min(1),
    message: z.string().min(1),
    reference: z.string().min(1),
  }).passthrough(),
}).passthrough()

export type CrmPersonReadQuery = z.infer<typeof crmPersonReadQuerySchema>
export type CrmPersonListResponse = z.infer<typeof crmPersonListResponseSchema>
export type CrmPersonHeaderResponse = z.infer<typeof crmPersonHeaderResponseSchema>
export type CrmPersonTimelineResponse = z.infer<typeof crmPersonTimelineResponseSchema>
export type CrmPersonParticipationResponse = z.infer<typeof crmPersonParticipationResponseSchema>
export type CrmPersonFeedbackResponse = z.infer<typeof crmPersonFeedbackResponseSchema>
export type CrmPersonMembershipsResponse = z.infer<typeof crmPersonMembershipsResponseSchema>
export type CrmPersonCommerceResponse = z.infer<typeof crmPersonCommerceResponseSchema>
export type CrmPersonCreditsResponse = z.infer<typeof crmPersonCreditsResponseSchema>
export type CrmPersonEnrichmentResponse = z.infer<typeof crmPersonEnrichmentResponseSchema>
export type CrmPersonArtifactsResponse = z.infer<typeof crmPersonArtifactsResponseSchema>
export type CrmPersonIdentityResponse = z.infer<typeof crmPersonIdentityResponseSchema>
export type CrmPersonRecommendationsResponse = z.infer<typeof crmPersonRecommendationsResponseSchema>
export type CrmPersonReadErrorEnvelope = z.infer<typeof crmPersonReadErrorEnvelopeSchema>
